import { useSelector } from "react-redux";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  HStack,
  Text,
  Badge,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  Select,
  Tag,
  TagCloseButton,
  TagLabel,
  useDisclosure,
} from "@chakra-ui/react";
import { IconDelete } from "../../../../../styles/icons";
import { postData, deleteData } from "../../../../utilities/axios";
import ManagePayment from "../../payment/ManagePayment";
import {
  useUtilityData,
  useUtilityDataSelector,
} from "../../../../utilities/contexts";
import VerificationStatusBox from "./VerificationStatusBox";
import { useRouter } from "next/router";
import { VerificationSuccessModal } from "../../../../components/modals/VerificationSuccessModal";
import { VerificationFailedModal } from "../../../../components/modals/VerificationFailedModal";
import { Button } from "@headlessui/react";
import Link from "next/link";

const statusColour = {
  Rejected: "red",
  "Verification in progress": "orange",
  Verified: "green",
};

const EditAssociations = ({ practitionerType }) => {
  const router = useRouter();

  const { verification } = router.query;
  const [associations, setAssociations] = useState([]);
  const [deletingAsc, setDeletingAsc] = useState(null);
  const [professionalBody, setProfessionalBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingAsc, setAddingAsc] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("UNPAID");
  const [openPaying, setOpenPaying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [intent, setIntent] = useState(null);

  const [noSubAlert, setOpenNoSubAlert] = useState(false);

  const associationsUtility = useUtilityDataSelector(
    (utilityData) => utilityData.therapistAssociations
  );
  const token = useSelector((state) => state.user.JWTToken);

  const cancelRef = useRef();
  const userDetails = useSelector((state) => state?.user?.userDetails);

  const profile = useMemo(() => userDetails.therapistProfile, [userDetails]);
  const utilityData = useUtilityData();

  const {
    isOpen: isVerificationSuccessOpen,
    onClose: onVerificationSuccessClose,
    onOpen: onVerificationSuccessOpen,
  } = useDisclosure();

  const {
    isOpen: isVerificationFailedOpen,
    onClose: onVerificationFailedClose,
    onOpen: onVerificationFailedOpen,
  } = useDisclosure();

  useEffect(() => {
    if (verification === "true") {
      onVerificationSuccessOpen();
    }

    if (verification === "false") {
      onVerificationFailedOpen();
    }
  }, [onVerificationFailedOpen, onVerificationSuccessOpen, verification]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    const ascList = [];
    for (const association of profile.associations) {
      let status = "Verification in progress";
      if (association.verified) {
        status = "Verified";
      }

      const asc = association;
      asc.status = status;
      ascList.push(asc);
    }

    setPaymentStatus(profile.paymentStatus);
    setAssociations(ascList);
  }, [profile]);

  const onDelete = async () => {
    setLoading(true);
    const data = {
      bodies: [deletingAsc],
      practitionerType,
    };

    await deleteData(
      token,
      data,
      `${process.env.NEXT_PUBLIC_NEW_USER_URL}bodies`
    );
    const ascList = associations.filter(
      (item) => item.value !== deletingAsc.value
    );
    setAssociations(ascList);
    setLoading(false);
    setDeletingAsc(null);
  };

  const selectAssociation = (e) => {
    setProfessionalBody(e.target.value);
    if (e.target.value === "") {
      return;
    }
    setProfessionalBody(e.target.value);
    let exists = addingAsc.find(
      (item) => item.value === parseInt(e.target.value)
    );
    let existsInList = associations.find(
      (item) => item.value === parseInt(e.target.value)
    );
    if (exists || existsInList) {
      return;
    }

    let newAsc = associationsUtility.find(
      (item) => item.value === parseInt(e.target.value)
    );
    const addingList = [...addingAsc];
    addingList.push(newAsc);
    setAddingAsc(addingList);
  };

  const unselectAssociation = (asc) => {
    const addingList = addingAsc.filter((item) => item.value !== asc.value);
    setAddingAsc(addingList);
  };

  const submitAdd = async () => {
    if (addingAsc.length === 0) {
      return;
    }
    setLoading(true);
    const data = {
      bodies: addingAsc,
      practitionerType,
    };

    await postData(
      token,
      data,
      `${process.env.NEXT_PUBLIC_NEW_USER_URL}bodies`
    );
    const currAsc = [...associations];
    for (const association of addingAsc) {
      currAsc.push({
        value: association.value,
        label: association.label,
        short: association.shortName,
        status: "Awaiting approval",
      });
    }
    setAssociations(currAsc);
    setLoading(false);
    setAddingAsc([]);
    setDeletingAsc(null);
    setProfessionalBody("");
  };

  const unusedAssociations = useMemo(
    () =>
      associationsUtility.filter(
        (asc) =>
          !addingAsc.find((add) => add.value === asc.value) &&
          !associations.find((sel) => sel.value === asc.value)
      ),
    [addingAsc, associationsUtility, associations]
  );

  const isLastAssociation = useMemo(() => {
    const verified = associations.filter((asc) => asc.verified);
    if (associations.length === 1 || verified.length === 1) {
      return {
        last: true,
        verified: verified.length > 0,
      };
    }
    return { last: false };
  }, [associations]);

  const subscribedPlan = useMemo(() => {
    const plans = utilityData.paymentPlans.filter(
      (plan) => plan.stripeId === userDetails.therapistProfile.paymentPlan
    );

    if (plans.length === 0) {
      return null;
    }

    const currPlan = plans[0];
    if (userDetails.therapistProfile.isPlanChanging) {
      currPlan.changing = true;
    }

    return currPlan;
  }, [
    userDetails.therapistProfile.paymentPlan,
    utilityData.paymentPlans,
    userDetails.therapistProfile.isPlanChanging,
  ]);

  const handleStartPay = () => {
    if (subscribedPlan !== null) {
      setOpenPaying(true);
    } else {
      setOpenNoSubAlert(true);
    }
  };

  return (
    <div className="flex flex-col items-center w-full mx-auto gap-2">
      <VerificationStatusBox
        paymentStatus={paymentStatus}
        paidDate={profile.verificationPaidAt}
        cancelledDate={profile.cancelledAt}
        onStartPay={handleStartPay}
        hasAssociations={associations.length > 0}
      />

      {associations.length === 0 && (
        <div className="rounded-md shadow w-full p-2 bg-red-500 flex justify-center">
          <span className="text-brand-off font-bold">
            To enable us to verify your account, please select at least one
            association
          </span>
        </div>
      )}

      {associations.map((item) => (
        <div className="rounded-lg shadow bg-white w-full" key={item.value}>
          <Box sx={{ p: 4 }}>
            <HStack spacing={2} justify={"space-between"}>
              <HStack spacing={2}>
                <Text>{item.label}</Text>
                <Badge
                  colorScheme={statusColour[item.status]}
                  className="rounded-md"
                >
                  {paymentStatus !== "PAID" ? "Awaiting payment" : item.status}
                </Badge>
              </HStack>
              <Button
                onClick={() => setDeletingAsc(item)}
                aria-label="Delete association"
                className="bg-red-500 p-3 shadow cursor-pointer rounded-full hover:bg-red-300 data-[disabled]:bg-red-300 data-[disabled]:cursor-not-allowed text-white flex items-center justify-center"
              >
                <IconDelete />
              </Button>
            </HStack>
          </Box>
        </div>
      ))}
      <div className="rounded-lg shadow bg-white w-full">
        <Select
          name="professionalBody"
          placeholder="Select a professional association to add"
          onChange={selectAssociation}
          value={professionalBody}
          fontSize="inherit"
        >
          {unusedAssociations.map((item, idx) => {
            return (
              <option className="text-base" key={idx} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </Select>
        {addingAsc.length > 0 && (
          <div className="p-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {addingAsc.map((item) => (
                <Tag
                  size={"lg"}
                  key={item.value}
                  variant={"solid"}
                  borderRadius={"full"}
                >
                  <TagLabel>{item.shortName}</TagLabel>
                  <TagCloseButton onClick={() => unselectAssociation(item)} />
                </Tag>
              ))}
            </div>
            {addingAsc.length > 0 && (
              <Button
                className="rounded-full bg-brand text-white font-bold px-4 py-2 hover:opacity-[0.7]"
                isLoading={loading}
                onClick={submitAdd}
                id="therapist-dashboard-add-association-pressed"
              >
                Add associations
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        isOpen={deletingAsc !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          if (!loading) {
            setDeletingAsc(null);
          }
        }}
      >
        <AlertDialogOverlay>
          {isLastAssociation.last &&
          (!isLastAssociation.verified || deletingAsc?.verified) ? (
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Unable to delete association
              </AlertDialogHeader>

              <AlertDialogBody>
                You cannot delete your last{" "}
                {isLastAssociation.verified && "verified"} association. If you
                wish to deactivate your account you can do so on the account
                settings page.
              </AlertDialogBody>

              <div className="flex w-full justify-between p-4">
                <Button
                  ref={cancelRef}
                  onClick={() => setDeletingAsc(null)}
                  className="px-4 py-2 font-bold bg-white hover:bg-gray-100 rounded-full"
                >
                  Cancel
                </Button>
                <Link
                  href="/accounts/common-account/account-settings"
                  prefetch={false}
                >
                  <Button className="bg-red-500 font-bold px-4 py-2 shadow rounded-full hover:bg-red-300 text-white">
                    Go to account settings
                  </Button>
                </Link>
              </div>
            </AlertDialogContent>
          ) : (
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Association
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to remove <b>{deletingAsc?.label}</b>? If
                you want to add this association again, you will need to wait
                for verification.
              </AlertDialogBody>

              <div className="flex w-full justify-between p-4">
                <Button
                  disabled={loading}
                  ref={cancelRef}
                  onClick={() => setDeletingAsc(null)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={loading}
                  onClick={onDelete}
                  className="bg-red-500 font-bold data-[disabled]:bg-red-300 data-[disabled]:cursor-not-allowed px-4 py-2 shadow rounded-full hover:bg-red-300 text-white"
                >
                  Delete
                </Button>
              </div>
            </AlertDialogContent>
          )}
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={noSubAlert} onClose={() => setOpenNoSubAlert(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              Please first select your subscription plan below
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={openPaying}
        leastDestructiveRef={cancelRef}
        onClose={() => setOpenPaying(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Make payment
            </AlertDialogHeader>

            <AlertDialogBody mb={3}>
              Begin your subscription for the yearly{" "}
              <span className="font-bold">
                £15<span className="text-xs font-base ml-[2px]">+VAT</span>
              </span>{" "}
              verification fee. This will redirect you to Stripe to setup your
              payment.
            </AlertDialogBody>

            <div className="flex justify-end px-4">
              <Button
                className="bg-brand text-white font-bold px-4 py-2 rounded-full hover:opacity-[0.7]"
                onClick={() => setPaying(true)}
                id="therapist-dashboard-confirm-pay-now-pressed"
              >
                Pay Now
              </Button>
            </div>

            <AlertDialogBody>
              {paying && (
                <ManagePayment
                  intentCache={intent}
                  onLoad={setIntent}
                  practitionerType={practitionerType}
                  paymentType={`${practitionerType}Verify`}
                  type={"subscription"}
                  redirect={window.location.href}
                />
              )}
            </AlertDialogBody>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <VerificationSuccessModal
        isSuccessOpen={isVerificationSuccessOpen}
        onSuccessClose={onVerificationSuccessClose}
      />
      <VerificationFailedModal
        isFailedOpen={isVerificationFailedOpen}
        onFailedClose={onVerificationFailedClose}
      />
    </div>
  );
};

export default EditAssociations;
